import { useLocation, useNavigate, useParams } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import { Field, SelectField, TextAreaField } from "./components/FormFields";
import { useOfferEntryForm } from "./hooks/useOfferEntryForm";
import LoyaltyQuillEditor from "./components/LoyaltyQuillEditor";

export default function AddOfferForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { brandId, offerId } = useParams();
  const initialValues = location.state?.offer || null;
  const {
    isEdit,
    form,
    error,
    fieldErrors,
    isSubmitting,
    handleFieldChange,
    handleImageChange,
    handleToggleBoolean,
    handleSubmit,
  } = useOfferEntryForm({
    brandId,
    offerId,
    initialValues,
    onSaved: () => navigate(`/services/loyalty-offers/${brandId}/offers`, { state: location.state || {} }),
  });

  return (
    <Layout>
      <main>
        <div className="mx-auto max-w-4xl my-2 px-4 lg:px-0">
          <div className="bg-white rounded-2xl shadow-lg">
            <div className="mx-auto p-6 bg-slate-50 rounded-t-2xl">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900">
                {isEdit ? "Edit Offer" : "Add Offer"}
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                {isEdit ? "Update the selected offer details." : "Create a new offer for this brand."}
              </p>
            </div>

            <form className="space-y-8 p-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <Field
                  id="title_en"
                  name="title_en"
                  label="Offer Title (EN) *"
                  value={form.title_en}
                  onChange={handleFieldChange}
                  placeholder="Enter offer title in English"
                  error={Boolean(fieldErrors.title_en)}
                  errorMessage={fieldErrors.title_en}
                />

                <Field
                  id="title_hi"
                  name="title_hi"
                  label="Offer Title (HI)"
                  value={form.title_hi}
                  onChange={handleFieldChange}
                  placeholder="Enter offer title in Hindi"
                  error={Boolean(fieldErrors.title_hi)}
                  errorMessage={fieldErrors.title_hi}
                  />

                <Field
                  id="tagline_en"
                  name="tagline_en"
                  label="Tagline (EN)"
                  value={form.tagline_en}
                  onChange={handleFieldChange}
                  placeholder="Enter offer tagline in English"
                />

                <Field
                  id="tagline_hi"
                  name="tagline_hi"
                  label="Tagline (HI)"
                  value={form.tagline_hi}
                  onChange={handleFieldChange}
                  placeholder="Enter offer tagline in Hindi"
                />

                <div className="md:col-span-2 grid grid-cols-1 gap-6 md:grid-cols-3">
                  <Field
                    id="save_amount"
                    name="save_amount"
                    label="Save Amount"
                    value={form.save_amount}
                    onChange={handleFieldChange}
                    placeholder="Enter savings amount"
                  />

                  <Field
                    id="redemption_price"
                    name="redemption_price"
                    label="Redemption Price"
                    type="number"
                    value={form.redemption_price}
                    onChange={handleFieldChange}
                    placeholder="Enter redemption price"
                    error={Boolean(fieldErrors.redemption_price)}
                    errorMessage={fieldErrors.redemption_price}
                  />

                  <SelectField
                    id="redemption_type"
                    name="redemption_type"
                    label="Redemption Type"
                    value={form.redemption_type}
                    onChange={handleFieldChange}
                    options={[
                      { value: "single", label: "Single" },
                      { value: "multiple", label: "Multiple" },
                    ]}
                    placeholder="Select redemption type"
                    error={Boolean(fieldErrors.redemption_type)}
                    errorMessage={fieldErrors.redemption_type}
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="offer_banner_file" className="block text-sm font-semibold text-gray-900">
                    Offer Banner Image {isEdit ? "(Optional)" : "*"}
                  </label>
                   {form.offer_banner ? (
                    <div className="mt-3 w-full max-w-md overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-2">
                      <img
                        src={form.offer_banner}
                        alt="Offer banner preview"
                        className="h-20 w-full rounded-lg object-cover"
                      />
                    </div>
                  ) : null}
                  <input
                    id="offer_banner_file"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    aria-invalid={Boolean(fieldErrors.offer_banner_file)}
                    className={`mt-1 block w-full rounded-xl border bg-white px-3 py-2 text-sm text-gray-700 file:mr-3 file:rounded file:border-0 file:bg-cyan-500 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white ${
                      fieldErrors.offer_banner_file ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-300"
                    }`}
                  />
                  {fieldErrors.offer_banner_file ? (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.offer_banner_file}</p>
                  ) : null}

                </div>

                {/* {!form.offer_banner_file ? (
                  <Field
                    id="offer_banner"
                    name="offer_banner"
                    label="Offer Banner URL"
                    value={form.offer_banner}
                    onChange={handleFieldChange}
                    placeholder="https://example.com/banner.jpg"
                  />
                ) : null} */}
                <div className="md:col-span-2">
                <Field
                  id="redirection_url"
                  name="redirection_url"
                  label="Redirection URL"
                  value={form.redirection_url}
                  onChange={handleFieldChange}
                  placeholder="https://example.com/offer"
                  error={Boolean(fieldErrors.redirection_url)}
                  errorMessage={fieldErrors.redirection_url}
                />
                </div>

                <Field
                  id="start_date"
                  name="start_date"
                  label="Start Date"
                  type="date"
                  value={form.start_date}
                  onChange={handleFieldChange}
                  error={Boolean(fieldErrors.start_date)}
                  errorMessage={fieldErrors.start_date}
                />

                <Field
                  id="end_date"
                  name="end_date"
                  label="End Date"
                  type="date"
                  value={form.end_date}
                  onChange={handleFieldChange}
                  error={Boolean(fieldErrors.end_date)}
                  errorMessage={fieldErrors.end_date}
                />


                <div className="md:col-span-2">
                  <LoyaltyQuillEditor
                    name="description_en"
                    label="Description (EN)"
                    value={form.description_en}
                    onChange={(value) => handleFieldChange({ target: { name: "description_en", value } })}
                    placeholder="Write description in English"
                    error={Boolean(fieldErrors.description_en)}
                    errorMessage={fieldErrors.description_en}
                  />
                </div>

                <div className="md:col-span-2">
                  <LoyaltyQuillEditor
                    name="description_hi"
                    label="Description (HI)"
                    value={form.description_hi}
                    onChange={(value) => handleFieldChange({ target: { name: "description_hi", value } })}
                    placeholder="Write description in Hindi"
                    error={Boolean(fieldErrors.description_hi)}
                    errorMessage={fieldErrors.description_hi}
                  />
                </div>
              </div>

              {/* <div className="flex flex-wrap items-center gap-6">
                <button
                  type="button"
                  onClick={() => handleToggleBoolean("featured")}
                  className={`inline-flex items-center rounded-full px-3 py-1.5 text-sm font-semibold ${
                    form.featured ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {form.featured ? "Featured" : "Not Featured"}
                </button>

                <button
                  type="button"
                  onClick={() => handleToggleBoolean("status")}
                  className={`inline-flex items-center rounded-full px-3 py-1.5 text-sm font-semibold ${
                    form.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {form.status === "active" ? "Active" : "Inactive"}
                </button>
              </div> */}

              {/* {error ? <p className="text-sm text-red-600">{error}</p> : null} */}

              <div className="flex flex-wrap gap-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center rounded-xl bg-cyan-500 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-600 disabled:cursor-not-allowed disabled:bg-cyan-300"
                >
                  {isSubmitting ? "Saving..." : isEdit ? "Save Offer" : "Add Offer"}
                </button>
                <button
                  type="button"
                  className="inline-flex items-center rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  onClick={() => navigate(`/services/loyalty-offers/${brandId}/offers`)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </Layout>
  );
}
